import React from 'react';
import { LayoutGrid, Plus, Trash2, ArrowRight, ArrowLeft, ArrowDown, Copy } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент ввода параметров произвольной формы
 * Позволяет добавлять несколько прямоугольных секций
 */
const ArbitraryShapeSection = ({ 
  formData, 
  customSections, 
  handleSectionChange, 
  handleAddSection, 
  handleRemoveSection, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-100 dark:border-cyan-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <LayoutGrid className="w-5 h-5 mr-2 text-cyan-600" />
        Размеры произвольной конструкции
      </h3>
      
      <div className="space-y-5">
        {customSections.map((section, index) => (
          <div key={index} className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-800 dark:text-white">
                Секция {index + 1}
              </h4>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  className="text-gray-400 hover:text-cyan-600 dark:text-gray-500 dark:hover:text-cyan-400 transition-colors"
                  onClick={() => {
                    // Дублировать секцию
                    const newSection = { ...section };
                    handleAddSection();
                    // Задержка для обновления state перед установкой значений
                    setTimeout(() => {
                      const newIndex = customSections.length;
                      Object.keys(newSection).forEach(key => {
                        handleSectionChange(newIndex, key, newSection[key]);
                      });
                    }, 10);
                  }}
                  title="Дублировать секцию"
                >
                  <Copy className="w-4 h-4" />
                </button>
                {customSections.length > 1 && (
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                    onClick={() => handleRemoveSection(index)}
                    title="Удалить секцию"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Длина секции */}
              <div>
                <label htmlFor={`section_${index}_length`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <ArrowRight className="w-3 h-3 mr-1 text-cyan-600" />
                  Длина (м)
                </label>
                <input
                  type="text"
                  id={`section_${index}_length`}
                  value={section.length}
                  onChange={(e) => handleSectionChange(index, 'length', e.target.value)}
                  className={`form-input w-full ${errors[`section_${index}_length`] ? 'input-error' : ''}`}
                  placeholder="Длина"
                />
                {errors[`section_${index}_length`] && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors[`section_${index}_length`]}
                  </p>
                )}
              </div>

              {/* Ширина секции */}
              <div>
                <label htmlFor={`section_${index}_width`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <ArrowLeft className="w-3 h-3 mr-1 text-cyan-600" />
                  Ширина (м)
                </label>
                <input
                  type="text"
                  id={`section_${index}_width`}
                  value={section.width}
                  onChange={(e) => handleSectionChange(index, 'width', e.target.value)}
                  className={`form-input w-full ${errors[`section_${index}_width`] ? 'input-error' : ''}`}
                  placeholder="Ширина"
                />
                {errors[`section_${index}_width`] && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors[`section_${index}_width`]}
                  </p>
                )}
              </div>

              {/* Высота секции */}
              <div>
                <label htmlFor={`section_${index}_height`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <ArrowDown className="w-3 h-3 mr-1 text-cyan-600" />
                  Высота (м)
                </label>
                <input
                  type="text"
                  id={`section_${index}_height`}
                  value={section.height}
                  onChange={(e) => handleSectionChange(index, 'height', e.target.value)}
                  className={`form-input w-full ${errors[`section_${index}_height`] ? 'input-error' : ''}`}
                  placeholder="Высота"
                />
                {errors[`section_${index}_height`] && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors[`section_${index}_height`]}
                  </p>
                )}
              </div>

              {/* Количество таких секций */}
              <div>
                <label htmlFor={`section_${index}_quantity`} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <Plus className="w-3 h-3 mr-1 text-cyan-600" />
                  Количество
                </label>
                <input
                  type="number"
                  id={`section_${index}_quantity`}
                  value={section.quantity}
                  onChange={(e) => handleSectionChange(index, 'quantity', e.target.value)}
                  className={`form-input w-full ${errors[`section_${index}_quantity`] ? 'input-error' : ''}`}
                  placeholder="Количество"
                  min="1"
                />
                {errors[`section_${index}_quantity`] && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors[`section_${index}_quantity`]}
                  </p>
                )}
              </div>
            </div>

            {/* Визуализация секции */}
            <div className="mt-3 flex justify-center">
              <div 
                className="bg-cyan-100 dark:bg-cyan-800 border border-cyan-300 dark:border-cyan-600" 
                style={{
                  width: `${Math.min(150, parseFloat(section.length || 0) * 20)}px`,
                  height: `${Math.min(100, parseFloat(section.width || 0) * 20)}px`,
                  position: 'relative'
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-cyan-800 dark:text-cyan-200">
                  {section.length}×{section.width}×{section.height} м
                  {section.quantity > 1 ? ` (×${section.quantity})` : ''}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Кнопка добавления новой секции */}
        <div className="flex justify-center">
          <Button
            type="button"
            onClick={handleAddSection}
            icon={<Plus className="w-4 h-4" />}
            variant="outline"
            size="sm"
          >
            Добавить секцию
          </Button>
        </div>

        {errors.customSections && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400 text-center">
            {errors.customSections}
          </p>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600">
        <p>Произвольная форма позволяет добавить несколько прямоугольных секций для сложных конструкций.</p>
        <p className="mt-1">Объем будет рассчитан как сумма объемов всех секций, а стоимость — с учетом общего объема.</p>
      </div>
    </div>
  );
};

export default ArbitraryShapeSection;